import { FormattedMessage, useIntl } from 'react-intl';
import { ViewPermissions } from '../Auth/ViewPermissions';
import {
  IconAdministrators,
  IconArrowLeft,
  IconDashboard,
  IconOrganisation,
  IconOrganisations,
  IconPayers,
  IconPeriods,
  IconReports,
  IconSearch,
  IconSettings,
  IconTicket,
  IconTravellers,
  IconUser,
} from '@/icons';
import { NavElement } from './NavElement';
import { useListOrganizationApplications } from '@/api/trafikportalen/trafikportalenAPI';
import { useMemo } from 'react';
import { useListOrganizations, useListTenants } from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth';
import { useTenantID } from '@/hooks/useTenantID';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useListAvailableUserProfiles } from '@/api/auth';
import { SecondaryMenu } from './SecondaryMenu';
import { ProfilePickerButton } from '../ProfilePickerButton';
import Button from '../core/Button';
import { useIncompletePayersCount } from '@/hooks/useIncompletePayersCount';
import { useOrganizationsMissingCurrentPeriod } from '@/hooks/useOrganizationsMissingCurrentPeriod';
import { useAccessToken } from '@/hooks/useAxiosAuth';
import { useConfig, useStudentsDisabled } from '@/contexts/ConfigContext';
import { useStudentNav } from '@/hooks/useStudentNav';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { isDepartmentUser } from '@/helpers/departmentPermissions';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';
import { useShowOrgLevelNav } from '@/hooks/useShowOrgLevelNav';

export function MainMenu() {
  const tenantId = useTenantID();
  const {
    userProfile,
    setUserProfile,
    previousUserProfile,
    preImpersonationLocation,
    setPreImpersonationLocation,
  } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const getAccessToken = useAccessToken();
  const { formatMessage } = useIntl();
  const { data: tenants } = useListTenants();
  const config = useConfig();
  const { data: profiles } = useListAvailableUserProfiles({
    application_uri: config.auth.applicationUri,
  });
  const matchRoute = useMatchRoute();
  const isUserProfileRoute = !!matchRoute({
    to: '/$tenantId/user',
    fuzzy: true,
  });
  const isImpersonating = !!userProfile?.expires_at;

  const profileCount = useMemo(() => {
    if (!profiles || !tenants) return 0;

    return profiles
      .filter((profile) =>
        tenants.some((tenant) => tenant.id === profile.tenant_id),
      )
      .filter((profile) => !profile.expires_at).length;
  }, [profiles, tenants]);

  const onQuitImpersonation = async () => {
    const targetHref = preImpersonationLocation;
    setPreImpersonationLocation(null);

    if (!previousUserProfile) {
      setUserProfile(null);
      navigate({ to: '/profiles', search: { tenantId } });
      return;
    }

    // Keep these back to back: a render in between would refetch the
    // cleared queries with the old profile.
    queryClient.clear();
    setUserProfile(previousUserProfile);

    if (targetHref) {
      // The restored profile's permissions load asynchronously after the
      // profile switch. Fetch the access token first so permission-gated
      // routes (e.g. /search) don't bounce back to the dashboard before
      // permissions are ready.
      try {
        await getAccessToken();
        await navigate({ href: targetHref });
      } catch {
        // Token refresh failed; getAccessToken already redirected to login.
      }
      return;
    }

    navigate({
      to: '/$tenantId',
      params: { tenantId: previousUserProfile.tenant_id },
    });
  };

  return (
    <>
      <nav
        aria-label={formatMessage({ defaultMessage: 'Huvudnavigation' })}
        className="flex size-full grow flex-col gap-1 overflow-auto p-4"
      >
        <PrimaryMenuItems />
        <div className="mb-auto" />
        <SecondaryMenu />
      </nav>

      {profileCount > 1 && !isImpersonating && <ProfilePickerButton />}

      <div className="w-full border-t-2 border-background-300" />
      {isImpersonating ? (
        <>
          <p className="px-3 text-text-700">
            <FormattedMessage defaultMessage="Du är inne på:" />{' '}
            <b>{userProfile.organization}</b>
          </p>
          <div className="flex h-24 w-full p-3">
            <Button
              variant="filled"
              className="size-full"
              onClick={onQuitImpersonation}
              startIcon={<IconArrowLeft />}
            >
              <FormattedMessage defaultMessage="Tillbaka" />
            </Button>
          </div>
        </>
      ) : userProfile ? (
        <>
          {userProfile.organization_id && userProfile.organization && (
            <p className="hyphens-auto break-words px-3 pt-2 text-center text-text-700">
              {userProfile.organization}
            </p>
          )}
          <Button
            variant={isUserProfileRoute ? 'filled' : 'ghost'}
            className="h-14 w-full rounded-none border-none"
            to="/$tenantId/user"
            params={{ tenantId }}
            startIcon={<IconUser />}
            data-tutorial="profile-button"
          >
            <FormattedMessage defaultMessage="Profilinställningar" />
          </Button>
        </>
      ) : null}
    </>
  );
}

const PrimaryMenuItems = () => {
  const isTenantViewer = checkUserPermissions({
    permission: 'tenant:viewer',
  });
  const isDeptUser = isDepartmentUser();
  const showOrgLevelNav = useShowOrgLevelNav();
  const studentsDisabled = useStudentsDisabled();

  const showBasicNav = showOrgLevelNav || (!isTenantViewer && isDeptUser);

  return (
    <>
      <NavElement
        to="/$tenantId"
        hiddenMobile={true}
        exactMatch={true}
        icon={<IconDashboard className="size-5" />}
        title={<FormattedMessage defaultMessage="Start" />}
      />
      {showBasicNav && (
        <NavElement
          to="/$tenantId/tickets"
          hiddenMobile={true}
          icon={<IconTicket className="size-5" />}
          title={<FormattedMessage defaultMessage="Biljetter" />}
          tutorialId="nav-tickets"
        />
      )}
      {showBasicNav && (
        <NavElement
          to="/$tenantId/reports"
          icon={<IconReports className="size-5" />}
          title={<FormattedMessage defaultMessage="Rapporter" />}
          tutorialId="nav-reports"
        />
      )}
      {showOrgLevelNav && (
        <>
          <NavElement
            to="/$tenantId/travellers"
            hiddenMobile={true}
            icon={<IconTravellers className="size-5" />}
            title={<FormattedMessage defaultMessage="Resenärer" />}
            tutorialId="nav-travellers"
          />
          <NavElement
            to="/$tenantId/departments"
            hiddenMobile={true}
            icon={<IconOrganisation className="size-5" />}
            title={<FormattedMessage defaultMessage="Organisation" />}
            tutorialId="nav-departments"
          />
        </>
      )}
      {showBasicNav && (
        <NavElement
          to="/$tenantId/administrators"
          icon={<IconAdministrators className="size-5" />}
          title={<FormattedMessage defaultMessage="Administratörer" />}
          tutorialId="nav-administrators"
        />
      )}

      {!studentsDisabled && (
        <ViewPermissions permission="organization:viewer" isSysadmin={false}>
          <StudentNavElements />
        </ViewPermissions>
      )}

      <ViewPermissions permission="tenant:viewer">
        <OrganizationsNavElement />

        {!studentsDisabled && <PeriodsNavElement />}

        <NavElement
          to="/$tenantId/search"
          icon={<IconSearch className="size-5" />}
          title={<FormattedMessage defaultMessage="Sök resenärer" />}
          tutorialId="nav-search"
        />
      </ViewPermissions>

      <ViewPermissions permission="tenant:viewer">
        <NavElement
          to="/$tenantId/reports"
          hiddenMobile={true}
          icon={<IconReports className="size-5" />}
          title={<FormattedMessage defaultMessage="Rapporter" />}
          tutorialId="nav-reports"
        />
      </ViewPermissions>

      <ViewPermissions permission="tenant:viewer">
        <NavElement
          to="/$tenantId/system-administrators"
          hiddenMobile={true}
          icon={<IconAdministrators className="size-5" />}
          title={<FormattedMessage defaultMessage="System­administratörer" />}
          tutorialId="nav-system-administrators"
        />
      </ViewPermissions>

      <ViewPermissions permission="tenant:viewer">
        <PayersNavElement />
      </ViewPermissions>

      <ViewPermissions permission="tenant:viewer">
        <NavElement
          to="/$tenantId/tenant-settings"
          icon={<IconSettings className="size-5" />}
          title={<FormattedMessage defaultMessage="Inställningar" />}
          tutorialId="nav-settings"
        />
      </ViewPermissions>
    </>
  );
};

const OrganizationsNavElement = () => {
  const { data: organizationApplications } = useListOrganizationApplications();

  const submittedApplications = useMemo(() => {
    if (!organizationApplications) return 0;

    return organizationApplications.filter(
      (application) => application.status === 'submitted',
    ).length;
  }, [organizationApplications]);

  return (
    <NavElement
      to="/$tenantId/organizations"
      hiddenMobile={true}
      icon={<IconOrganisations className="size-5" />}
      title={<FormattedMessage defaultMessage="Organisationer" />}
      notificationCount={submittedApplications}
      tutorialId="nav-organizations"
    />
  );
};

const PeriodsNavElement = () => {
  const { data: organizations } = useListOrganizations();
  const { total } = useOrganizationsMissingCurrentPeriod(organizations);

  return (
    <NavElement
      to="/$tenantId/periods"
      hiddenMobile={true}
      icon={<IconPeriods className="size-5" />}
      title={<FormattedMessage defaultMessage="Perioder" />}
      notificationCount={total}
      tutorialId="nav-periods"
    />
  );
};

const PayersNavElement = () => {
  const incompletePayersCount = useIncompletePayersCount();

  return (
    <NavElement
      to="/$tenantId/payers"
      icon={<IconPayers className="size-5" />}
      title={<FormattedMessage defaultMessage="Faktura­mottagare" />}
      notificationCount={incompletePayersCount}
      tutorialId="nav-payers"
    />
  );
};

const StudentNavElements = () => {
  const accessibleCategories = useAccessibleStudentCategories();
  const getStudentNavs = useStudentNav();

  const navItems = getStudentNavs(accessibleCategories);
  return navItems.map(({ to, icon, defaultMessage: title, tutorialId }) => (
    <NavElement
      key={to}
      to={to}
      icon={icon}
      title={title}
      tutorialId={tutorialId}
    />
  ));
};
